import * as React from 'react'
import { ReactElement } from 'react'
import { Props as MonitoringBuiltInDashboardsProps } from '@mulesoft/monitoring-center-components/lib/components/BuiltInDashboards/BuiltInDashboards'
import {
  BuiltInDashboards as MonitoringBuiltInDashboard,
  DashboardError,
  DashboardLoadingState,
  DashboardRow
} from '@mulesoft/monitoring-center-components'
import { Props as ResourceDetailProps } from '@mulesoft/monitoring-center-components/lib/components/ResourceDetail/ResourceDetail'
import { buildLogSearchRedirectionLink, LogSearchLinkType } from '@mulesoft/monitoring-center-components/lib/utils/LogSearchRedirectionHelper'
import { AutoRefreshFrequency } from '@mulesoft/monitoring-center-components/lib/components/Toolbar/AutoRefresh/model'
import { TimeRange } from '@mulesoft/monitoring-center-components/lib/components/BuiltInDashboards/PreBuilt'
import { MenuItemValue } from '@mulesoft/monitoring-center-components/lib/components/Menu/MenuItemValue'
import {
  isHybridApp,
  isManagedAPI,
  isRTFApp,
  isTarget,
  ResourceType,
  ResourceType as ComponentsResourceType
} from '@mulesoft/monitoring-center-components/lib/models/resource'
import { ActionButton } from '@mulesoft/monitoring-center-components/lib/models/actions-bar'
import { BuiltInDashboardSaveAsModal } from './SaveAs/SaveAsModal/BuiltInSaveAsModal'
import { transformToEnvironmentSelectorProps } from '../../common/environment/model'
import { Props as TooltipProps } from '@mulesoft/anypoint-components/lib/Tooltip'
import { transformToVersionSelectorProps } from '../../common/version/model'
import { EnvironmentSelectorState } from '../../common/environment/reducer'
import { FeaturesCompatibility } from '../../common/compatibility/model'
import { ResourceSelectorState } from '../../common/resource/reducer'
import { DateT } from '@mulesoft/anypoint-components/lib/DatePicker'
import { VersionSelectorState } from '../../common/version/reducer'
import { Profile } from '../../remote-api/CoreServicesRemoteApi'
import { getTimeOptions } from '../TimePickerNavbar/TimeOption'
import { DetailedResourceState , getDashboards } from './model'
import { exceedsMaxWindow, MAX_SELECTABLE_DAYS } from './dateWindow'
import { dashboardRegistry , getLabel, isDashboardNotSupportedForApiAnalyticsTypes} from './dashboardType'
import { API , Environment} from '../../remote-api/models'
import { CustomMetric } from './CustomMetrics/model'
import { MonitoringToasts } from '../Toasts/Toasts'
import { isPCEOrGov } from '../../util/environments'
import { Toast } from '../Toasts/model'
import MainSelector from '@mulesoft/monitoring-center-components/lib/components/Selector/MainSelector/MainSelector'
import DateType from '@mulesoft/monitoring-center-components/lib/utils/date/DateType'
import SuccessSaveAsMessage from './SaveAs/SuccessSaveAsMessage/SuccessSaveAsMessage'
import NotificationSwitcher from './NotificationSwitcher'
import visualizerApi from '../../util/VisualizerApi'
import {
  BuiltInDashboardsHeaderProps
} from '@mulesoft/monitoring-center-components/lib/components/BuiltInDashboards/BuiltInDashboardsHeader/BuiltInDashboardsHeader'
import {
  DetailedResource,
  getComponentsResourceType,
  getComponentsResourceTypeFromResource,
  getResourceId,
  isAPIDetail,
  Resource,
  transformDetailedResourceToComponentsResource,
  transformResourceToResourceSelector,
  transformToResourceSelectorProps
} from '../../common/resource/model'
import './BuiltInDashboard.css'
import { getSupportedRuntimesText } from '../../util/SupportedRuntimesHelper'
import * as labels from '@mulesoft/monitoring-center-components/lib/utils/labels'
import {
  DashboardItem
} from '@mulesoft/monitoring-center-components/lib/components/BuiltInDashboards/BuiltInDashboardsContent/BuiltInDashboardsContent';
import { isFlex } from '../../util/flex';
import { shouldShowApiSwitch } from './common/utils'
import FeatureFlags from '../../types/featureflags';
import FeatureFlagService from '../../services/FeatureFlagService';
import { Labels } from '../../common/labels/model';
import CertificatesExpiryBanner from '../CertificatesExpiryBanner/CertificatesExpiryBanner'
import OsdLogsSearchPageModeEnum from "../../types/OsdLogsSearchPageModeEnum";
import Urls from "../Sidebar/Urls";

export type LoadProps = {
  dashboardType: string
  envId: string
  resourceId: string
  resourceType: string
  versionId?: string
  hasDashboardTypeChanged?: boolean
  targetId?: string
}

export type OnViewProps = LoadProps & {environmentSelector: EnvironmentSelectorState, componentsResourceType: ResourceType}

export type ExternalValueProps = {
  environmentId: string
  resourceId: string
  dashboardType: string
  resourceType: string
  versionId?: string
  targetId?: string
}

export type ValueProps = {
  labels?: Labels,
  orgDomain: string
  organizationId: string
  isTitanium: boolean
  isLogPointsCompatible?: boolean
  environmentSelector: EnvironmentSelectorState
  resourceSelector: ResourceSelectorState
  versionSelector: VersionSelectorState
  details: DetailedResourceState
  range: TimeRange
  toasts: Toast[]
  metrics?: CustomMetric[]
  autoRefresh: {
    checked: boolean,
    frequency?: AutoRefreshFrequency
  }
  featuresCompatibility?: FeaturesCompatibility
  profile?: Profile
  hasEditRole?: boolean
}

export type EventProps = {
  load: (props: LoadProps & {componentsResourceType: ComponentsResourceType}) => void
  rangeChange: (range: TimeRange) => void
  saveDashboard: (dashboardType: string) => void
  addToast: (toast: Toast) => void
  viewLogs: (logSearchLink: string) => void
  manageAPI: (orgDomain: string, organizationId: string, environmentId: string, resource?: DetailedResource) => void
  manageApplication: (orgDomain: string, organizationId: string, environmentId: string, resource?: DetailedResource) => void
  manageTarget: (orgDomain: string, organizationId: string, environmentId: string, resource?: DetailedResource) => void
  configureLogPoints: (environmentId: string, resource: Resource, versionId?: string) => void
  saveAs: (dashboardType: string) => void
  savedAsCustomDashboard: (dashboardType: string) => void
  environmentSelected: (environmentId: string, environments?: Environment[]) => void
  resourceSelected: (resourceId: string, resourceType: ResourceType, resources?: Resource[]) => void
  versionSelected: (versionId: string, versions: API[]) => void
  view: (props: OnViewProps) => void
  mainSelectorOpen: (
    environment?: { selected: Environment, list: Environment[] },
    resource?: { selected: Resource, list: Resource[] },
    version?: { selected: API, list: API[] }
  ) => void
  notifyIframeError: (error: any) => void
  onDetailHide: (isDetailHidden: boolean) => void
  onAutoRefreshToggle: (checked: boolean, frequency: AutoRefreshFrequency) => void
  dispatchViewedBuiltIn: (resource: Resource, versionId?: string) => void
  viewCompatibility: (organizationId: string, environmentId: string, resourceType: string, resId: string) => void
  fetchLabels: () => void
}

export type Props = ValueProps & ExternalValueProps

export type AllProps = Props & EventProps

export type State = {
  saveAsModalOpened: boolean
  saveAsErrorName: boolean
  saveAsLoading: boolean
  dashboardName: string
  environment?: {
    selected: Environment
    list: Environment[]
  }
  resource?: {
    selected: Resource
    list: Resource[]
  }
  version?: {
    selected: API
    list: API[]
  }
  dashboardState: {
    isRefreshing: boolean
    state: DashboardLoadingState
    disableMenu: boolean
  }
  viewedResource?: string
  viewedVersion?: string
  apiMetricsView?: boolean
}

export class BuiltInDashboard extends React.PureComponent<AllProps, State> {

  static renderSuccessSaveAsMessage(dashboardName: string, dashboardNameSlug: string) {
    return (<SuccessSaveAsMessage dashboardName={dashboardName} dashboardNameSlug={dashboardNameSlug}/>)
  }

  constructor(props: AllProps) {
    super(props)

    this.state = {
      apiMetricsView: isDashboardNotSupportedForApiAnalyticsTypes(props.dashboardType),
      saveAsModalOpened: false,
      saveAsErrorName: false,
      saveAsLoading: false,
      dashboardName: '',
      dashboardState: {
        isRefreshing: false,
        state: DashboardLoadingState.LOADING,
        disableMenu: true
      }
    }

    this.handleDispatchViewedBuiltIn = this.handleDispatchViewedBuiltIn.bind(this)
    this.onSaveAsClick = this.onSaveAsClick.bind(this)
    this.handleSaveAsModalClose = this.handleSaveAsModalClose.bind(this)
    this.handleSaveAsModalConfirmation = this.handleSaveAsModalConfirmation.bind(this)
    this.saveOnClick = this.saveOnClick.bind(this)
    this.saveAsSuccess = this.saveAsSuccess.bind(this)
    this.saveAsError = this.saveAsError.bind(this)
    this.environmentSelected = this.environmentSelected.bind(this)
    this.resourceSelected = this.resourceSelected.bind(this)
    this.versionSelected = this.versionSelected.bind(this)
    this.onView = this.onView.bind(this)
    this.onOpen = this.onOpen.bind(this)

    this.onPillMenuChange = this.onPillMenuChange.bind(this)

    this.getActionsBarButtons = this.getActionsBarButtons.bind(this)

    this.onTimeChange = this.onTimeChange.bind(this)
    this.checkUrlTimeRange = this.checkUrlTimeRange.bind(this)
    this.hasUrlChanged = this.hasUrlChanged.bind(this)

    this.onAutoRefreshToggle = this.onAutoRefreshToggle.bind(this)

    this.onDashboardStateChange = this.onDashboardStateChange.bind(this)
    this.onDashboardError = this.onDashboardError.bind(this)

    this.getMainSelector = this.getMainSelector.bind(this)
    this.getSaveAsModalProps = this.getSaveAsModalProps.bind(this)
    this.getHeaderProps = this.getHeaderProps.bind(this)
    this.getResourceDetailProps = this.getResourceDetailProps.bind(this)
    this.switchAPIDashboard = this.switchAPIDashboard.bind(this)
    this.getDashboardsProps = this.getDashboardsProps.bind(this)
  }

  handleDispatchViewedBuiltIn(resource?: Resource, versionId?: string) {
    if (resource &&
      (getResourceId(resource) !== this.state.viewedResource || versionId !== this.state.viewedVersion && this.props.versionSelector.selected)
    ) {
      this.props.dispatchViewedBuiltIn(resource, versionId)
      this.setState({...this.state, viewedResource: getResourceId(resource), viewedVersion: versionId})
    }
  }

  // Listeners
  removeCloneListeners() {
    visualizerApi.removeListener('clone-success', this.saveAsSuccess)
    visualizerApi.removeListener('clone-error', this.saveAsError)
  }

  addCloneListeners() {
    visualizerApi.addListener('clone-success', this.saveAsSuccess)
    visualizerApi.addListener('clone-error', this.saveAsError)
  }

  // Action links handlers
  onViewLogsClick(logsSearchLink: string) {
    this.props.viewLogs(logsSearchLink)
  }

  onManageAPIClick(orgDomain: string, organizationId: string, environmentId: string, resource?: DetailedResource) {
    this.props.manageAPI(orgDomain, organizationId, environmentId, resource)
  }

  onManageApplicationClick(orgDomain: string, organizationId: string, environmentId: string, resource?: DetailedResource) {
    this.props.manageApplication(orgDomain, organizationId, environmentId, resource)
  }

  onManageTargetClick(orgDomain: string, organizationId: string, environmentId: string, resource?: DetailedResource) {
    this.props.manageTarget(orgDomain, organizationId, environmentId, resource)
  }

  onConfigureLogPointsClick(environmentId: string, resource: Resource, versionId?: string) {
    this.props.configureLogPoints(environmentId, resource, versionId)
  }

  // Save as handlers
  onSaveAsClick() {
    this.props.saveAs(this.props.dashboardType)
    this.setState({saveAsModalOpened: true})
  }

  handleSaveAsModalClose() {
    this.removeCloneListeners()
    this.setState({...this.state, saveAsModalOpened: false, saveAsErrorName: false, saveAsLoading: false})
  }

  handleSaveAsModalConfirmation = (dashboardName: string) => {
    this.addCloneListeners()
    this.setState({...this.state, dashboardName, saveAsLoading: true})
    visualizerApi.clone(dashboardName, undefined, this.props.environmentId)
  }

  saveOnClick = (type: string) => {
    this.props.saveDashboard(type)
  }

  saveAsSuccess(payload: any) {
    const {dashboardName} = this.state
    const dashboardNameSlug = payload.detail

    this.props.addToast({
      children: BuiltInDashboard.renderSuccessSaveAsMessage(dashboardName, dashboardNameSlug),
      kind: 'success',
      testId: 'save-as-toast-success'
    })
    this.props.savedAsCustomDashboard(this.props.dashboardType)
    this.handleSaveAsModalClose()
  }

  saveAsError(payload: any) {
    const errorStatus = payload.detail
    if (errorStatus === 'name-exists') {
      this.setState({...this.state, saveAsErrorName: true, saveAsLoading: false})
    } else {
      this.props.addToast({title: 'Error while saving dashboard', kind: 'error'})
      this.handleSaveAsModalClose()
    }
  }

  // Selector handlers
  environmentSelected(environmentId: string) {
    this.props.environmentSelected(environmentId, this.props.environmentSelector.resources)
  }

  resourceSelected(resourceId: string, resourceType: ResourceType) {
    this.props.resourceSelected(resourceId, resourceType, this.props.resourceSelector.resources)
  }

  versionSelected(versionId: string) {
    const {versionSelector} = this.props
    if (versionSelector.resources) {
      this.props.versionSelected(versionId, versionSelector.resources)
    }
  }

  onView(envId: string, resourceId: string, componentsResourceType: ResourceType, versionId?: string) {
    const {environment, resource, version} = this.state
    const {environmentSelector, resourceSelector, resourceType, targetId, dashboardType} = this.props

    if (
      (environment && envId !== environment.selected.id) ||
      (resource && resourceId !== getResourceId(resource.selected)) ||
      (version && versionId !== version.selected.id.toString())
    ) {
      this.setState({...this.state, environment: undefined, resource: undefined, version: undefined}, () => {
        const resourceSelected = resourceSelector.resources && resourceSelector.resources.find((res: Resource) =>
          getResourceId(res) === resourceId && getComponentsResourceTypeFromResource(res) === componentsResourceType
        )
        if (resourceSelected) this.handleDispatchViewedBuiltIn(resourceSelected, versionId)

        this.props.view({dashboardType, envId, resourceId, resourceType, versionId, targetId, environmentSelector, componentsResourceType})
      })
    }
  }

  onOpen() {
    const {environment, resource, version} = this.state
    this.props.mainSelectorOpen(environment, resource, version)
  }

  // Pill menu handlers
  onPillMenuChange(item: MenuItemValue) {
    const {environmentSelector, environmentId, resourceSelector, resourceId, resourceType, versionId, targetId} = this.props
    const resId = resourceSelector.selected ? getResourceId(resourceSelector.selected) : resourceId
    const hasDashboardTypeChanged = true
    const dashboardType = item.key

    const props = {envId: environmentId, resourceId: resId, resourceType, versionId, targetId, dashboardType, hasDashboardTypeChanged}
    this.props.view({...props, environmentSelector, componentsResourceType: getComponentsResourceType(resourceType)})
  }

  // Actions bar handlers
  getActionsBarButtons(): ActionButton[] {
    const {
      isTitanium,
      isLogPointsCompatible,
      metrics,
      dashboardType,
      versionId,
      details,
      organizationId,
      environmentId,
      resourceSelector,
      resourceType,
      range,
      orgDomain,
      hasEditRole
    } = this.props
    const {dashboardState} = this.state
    const dashState = dashboardState.state

    const dashDisabled =
      dashState !== DashboardLoadingState.DISPLAYED ||
      (dashboardType === 'custom-metrics' && (!metrics || metrics.length === 0))

    let actionButtons: ActionButton[] = []
    if (dashboardType === dashboardRegistry.applicationNetwork.key) {
      return actionButtons
    }
    const selectedResource = details.detailedResource ?
      transformDetailedResourceToComponentsResource(organizationId, environmentId, details.detailedResource) :
      undefined

    if (selectedResource) {
      const appId = isRTFApp(selectedResource) || isHybridApp(selectedResource) ? selectedResource.name : selectedResource.id.toString()
      const urlHashArray = window.location.hash.split('/')
      const logLevel = urlHashArray[urlHashArray.length - 1].toLowerCase() === 'failures' ? 'ERROR' : undefined
      const shouldNotShowLogsLink = (resourceType === 'api' && !isFlex(selectedResource)) || !isTitanium

      if (!shouldNotShowLogsLink && !isPCEOrGov() && hasEditRole) {
        let applicationFilterId = !isTarget(selectedResource) && appId ? appId : selectedResource.id.toString();
        let applicationFilterName = isTarget(selectedResource) ? selectedResource.name : undefined;

        if (isFlex(selectedResource) && ('deployment' in selectedResource) && selectedResource.deployment && selectedResource.deployment.targetId) {
          applicationFilterId = `'${selectedResource.deployment.targetId}'`;
          applicationFilterName = selectedResource.deployment.targetName || undefined;
        }

        const osdLogsSearchPageEnabled = FeatureFlagService
            .getStringFlag(FeatureFlags.OSD_LOG_SEARCH_PAGE_MODE) === OsdLogsSearchPageModeEnum.ENABLED


        const logsLinkProps = {
          key: !isTarget(selectedResource) ? 'appId' : 'workerId',
          value: applicationFilterId,
          logLevel: logLevel,
          from: range.from,
          to: range.to,
          resolvedName: applicationFilterName,
          linkType: osdLogsSearchPageEnabled ? 'osd': 'kibana' as LogSearchLinkType,
          prefixPath: osdLogsSearchPageEnabled ? `${Urls.modernMonitoring}/organizations/${organizationId}`: '#'
        }
        const logsSearchLink = buildLogSearchRedirectionLink(logsLinkProps)

        actionButtons.push({
          behaviour: 'link',
          label: 'View logs',
          show: isTitanium && !dashDisabled,
          icon: {name: 'link', size: 18},
          testId: 'view-logs-link',
          onClick: () => this.onViewLogsClick(logsSearchLink)
        })
      }

      actionButtons = actionButtons.concat([{
          behaviour: 'link',
          label: 'Manage API',
          show: (versionId !== undefined && !dashDisabled) || !!(selectedResource && isManagedAPI(selectedResource)),
          icon: {name: 'link', size: 18},
          testId: 'manage-api-link',
          onClick: () => this.onManageAPIClick(orgDomain, organizationId, environmentId, details.detailedResource)
        },
        {
          behaviour: 'link',
          label: 'Manage application',
          show: !versionId && !dashDisabled && !isTarget(selectedResource) && !(selectedResource && isManagedAPI(selectedResource)),
          icon: {name: 'link', size: 18},
          testId: 'manage-application-link',
          onClick: () => this.onManageApplicationClick(orgDomain, organizationId, environmentId, details.detailedResource)
        },
        {
          behaviour: 'link',
          label: 'Manage target',
          show: !versionId && !dashDisabled && isTarget(selectedResource),
          icon: {name: 'link', size: 18},
          testId: 'manage-target-link',
          onClick: () => this.onManageTargetClick(orgDomain, organizationId, environmentId, details.detailedResource)
        }]
      )
    }

    const resource = resourceSelector.selected

    if (resource && (isLogPointsCompatible || isFlex(resource, versionId)) && !isPCEOrGov() && hasEditRole) {
      actionButtons.push({
        behaviour: 'link',
        label: 'Configure log points',
        show: isTitanium && !dashDisabled && !(selectedResource && (isManagedAPI(selectedResource) || isTarget(selectedResource))),
        icon: {name: 'link', size: 18},
        testId: 'configure-log-points-link',
        onClick: () => this.onConfigureLogPointsClick(environmentId, resource, versionId)
      })
    }

    return actionButtons.concat([
      {
        behaviour: 'button',
        label: 'Save as',
        disabled: dashDisabled || !hasEditRole,
        show: isTitanium,
        icon: {name: 'duplicate-small', size: 15},
        testId: 'save-as-button',
        onClick: () => this.onSaveAsClick(),
        tooltip: !hasEditRole ? {
          content: labels.missingDashboardPermissions,
          position: 'top'
        } : undefined
      }
    ])
  }

  // Time handlers
  onTimeChange(time: DateT) {
    const isNinetyDayWindowEnabled = FeatureFlagService.get(FeatureFlags.BUILT_IN_DASHBOARD_90_DAY_WINDOW_ENABLED)
    if (isNinetyDayWindowEnabled && exceedsMaxWindow(time, MAX_SELECTABLE_DAYS)) {
      this.props.addToast({
        title: `Date ranges over ${MAX_SELECTABLE_DAYS} days are not supported. Select a date range that is within ${MAX_SELECTABLE_DAYS} days.`,
        kind: 'error',
        testId: 'date-window-error-toast'
      })
      return
    }
    this.props.rangeChange(DateType.formatRange({value: time}))
  }

  // Refresh handlers
  onAutoRefreshToggle(checked: boolean, frequency: AutoRefreshFrequency) {
    this.props.onAutoRefreshToggle(checked, frequency)
  }

  // Dashboard handlers
  onDashboardStateChange(loadingState: DashboardLoadingState) {
    switch (loadingState) {
      case DashboardLoadingState.ERROR:
      case DashboardLoadingState.DISPLAYED:
        this.setState({
          ...this.state,
          dashboardState: {
            isRefreshing: false,
            disableMenu: false,
            state: loadingState
          }
        })
        break
      case DashboardLoadingState.LOADING:
      case DashboardLoadingState.LOADED:
      default:
        this.setState({...this.state, dashboardState: {...this.state.dashboardState, state: loadingState}})
        break
    }
  }

  onDashboardError(error: DashboardError) {
    this.props.notifyIframeError(error)
  }

  // Props
  getMainSelector() {
    const {environmentSelector, resourceSelector, versionSelector, resourceType} = this.props;
    const {environment, resource, version} = this.state;
    const selected = environment && resource ? {
      environmentName: environment.selected.name,
      resource: transformResourceToResourceSelector(resource.selected),
      version: version ? version.selected : undefined
    } : undefined;
    let isLoading = true;
    if (resource && resource.selected) {
      isLoading = resourceType !== 'api' ? false : (version === undefined || !version.list)
    }

    const props = {
      environmentSelector: transformToEnvironmentSelectorProps(environmentSelector, 35),
      resourceSelector: transformToResourceSelectorProps(resourceSelector, 52),
      versionSelector: transformToVersionSelectorProps(versionSelector, 55),
      environmentSelected: this.environmentSelected,
      resourceSelected: this.resourceSelected,
      versionSelected: this.versionSelected,
      onView: this.onView,
      maxLength: 35,
      isLoading: isLoading,
      selected: selected,
      onOpen: this.onOpen,
      supportedRuntimesTooltipText: getSupportedRuntimesText()
    }

    return (
      <MainSelector {...props}/>
    )
  }

  getSaveAsModalProps(selectedTab?: MenuItemValue, dashboards?: DashboardItem[]) {
    return {
      opened: this.state.saveAsModalOpened,
      nameExists: this.state.saveAsErrorName,
      isLoading: this.state.saveAsLoading,
      hasMapPanel: selectedTab && dashboards ? this.selectedTabHasMapPanel(selectedTab, dashboards) : false,
      onClose: this.handleSaveAsModalClose,
      onApply: this.handleSaveAsModalConfirmation
    }
  }

  selectedTabHasMapPanel(selectedTab: MenuItemValue, dashboards: DashboardItem[]) {
    let hasMapPanel = false
    let selectedDashboard = dashboards.find(dashboard => dashboard.item.key === selectedTab.key);
    if (selectedDashboard && selectedDashboard.get()) {
      const dashProps = (selectedDashboard.get() as ReactElement<any>).props

      if (dashProps && dashProps.dashboardData && dashProps.dashboardData.rows) {
        let rows: DashboardRow[] = dashProps.dashboardData.rows
        rows.forEach(row => {row.panels.forEach(panel => {if (panel.type === 'grafana-worldmap-panel') hasMapPanel = true})})
      }
    }
    return hasMapPanel
  }

  getNotificationSwitcherProps() {
    const {organizationId, orgDomain, environmentId, details} = this.props
    const {detailedResource} = details

    return {organizationId, orgDomain, environmentId, detailedResource}
  }

  getHeaderProps(): BuiltInDashboardsHeaderProps {
    const {range, isTitanium, resourceType, autoRefresh, dashboardType} = this.props
    const timeOptionsArr = getTimeOptions(isTitanium, resourceType)

    return {
      title: this.getMainSelector(),
      fullscreenRootClassName: 'app anypoint-styles',
      visualizerTimePickerProps: {
        onTimeChange: this.onTimeChange,
        time: range,
        predefinedDates: timeOptionsArr
      },
      hideAutoRefresh: isTitanium ? undefined : true,
      onAutoRefreshToggle: this.onAutoRefreshToggle,
      autoRefreshDisabled: dashboardType === dashboardRegistry.applicationNetwork.key,
      autoRefreshChecked: autoRefresh.checked,
      autoRefreshFrequency: autoRefresh.frequency,
      autoRefreshPopOverMsg: `Use manual refresh for ${getLabel(dashboardType)} dashboard`,
      onRefresh: undefined,
      onZoomOut: undefined,
      downArrowClick: undefined,
      upArrowClick: undefined
    }
  }

  getResourceDetailProps(): ResourceDetailProps {
    const {details, organizationId, environmentId, onDetailHide} = this.props
    const selectedResource = details.detailedResource ?
      transformDetailedResourceToComponentsResource(organizationId, environmentId, details.detailedResource) :
      undefined

    return {
      resource: selectedResource,
      loading: details.isFetching,
      hidden: details.hidden,
      onHide: onDetailHide,
    }
  }

  switchAPIDashboard() {
    const {apiMetricsView} = this.state
    this.setState({...this.state, apiMetricsView: !apiMetricsView})
  }

  getDashboardsProps(): MonitoringBuiltInDashboardsProps {
    const {isTitanium, range, dashboardType, featuresCompatibility, environmentId, versionId, profile} = this.props
    const {dashboardState, apiMetricsView} = this.state
    const resourceDetail = this.getResourceDetailProps()
    const escapedRange = DateType.escapeTime(range)
    const dashboards = resourceDetail.resource ? getDashboards(
      profile,
      resourceDetail.resource,
      escapedRange,
      {onDashboardStateChange: this.onDashboardStateChange, onDashboardError: this.onDashboardError},
      isTitanium,
      dashboardType,
      environmentId,
      featuresCompatibility,
      versionId,
      apiMetricsView
    ) : undefined
    const selected = dashboards && dashboards.find(dashboard => dashboard.item.key === dashboardType)

    const isApiAnalyticsBuiltInDashboardDisabled = FeatureFlagService.get(FeatureFlags.API_ANALYTICS_BUILT_IN_DASHBOARD_DISABLED)
    const apiMetricsCompatible = featuresCompatibility && featuresCompatibility.apiMetrics.compatible
    const isApi = resourceDetail.resource && isAPIDetail(resourceDetail.resource as DetailedResource)
    const isFlexApi = resourceDetail.resource && isFlex(resourceDetail.resource)
    // If API_ANALYTICS_BUILT_IN_DASHBOARD_DISABLED feature flag is true, do not show the link to switch between basic and enhanced metrics
    const apiSwitch = shouldShowApiSwitch(isApiAnalyticsBuiltInDashboardDisabled, apiMetricsCompatible, isApi) ? {
      label: apiMetricsView ? 'Switch to basic metrics' : 'Show enhanced metrics view',
      onClick: this.switchAPIDashboard
    } : undefined
    const apiSwitchTooltip: TooltipProps | undefined = apiMetricsCompatible && isApi && apiMetricsView ? {
      className: 'api-switch-tooltip',
      offset: [0, 1],
      content: 'You can always come back to this view',
      position: 'bottom'
    } : undefined

    const actionsBarButtons: ActionButton[] = this.getActionsBarButtons()

    return {
      headerProps: this.getHeaderProps(),
      resourceDetail: resourceDetail,
      actions: actionsBarButtons.length !== 0 ? actionsBarButtons : undefined,
      isTitanium: isTitanium,
      dashboards: dashboards,
      apiSwitch: isPCEOrGov() || isFlexApi ? undefined : apiSwitch,
      apiSwitchTooltip: undefined, // because the API Switch has been turned off using LD, there is no need to show a tooltip
      selected: selected ? selected.item : undefined,
      disableMenu: dashboardState.disableMenu,
      isRefreshing: dashboardState.isRefreshing,
      dashboardState: dashboardState.state,
      onPillClick: this.onPillMenuChange,
      profile: profile,
    }
  }

  checkUrlTimeRange() {
    const urlArray = window.location.href.split('?')
    if (urlArray.length > 1) {
      const queryParams = new URLSearchParams('?' + urlArray[1])
      const from = queryParams.get('from')
      const to = queryParams.get('to')
      if (from && to) {
        const formattedFrom = DateType.formatVisualizerDate(from)
        const formattedTo = DateType.formatVisualizerDate(to)
        if (formattedFrom && formattedTo) this.props.rangeChange({from: formattedFrom, to: formattedTo})
      }
    }
  }

  private hasUrlChanged(prevProps: Readonly<AllProps>) {
    const {environmentId, resourceId, targetId, versionId, dashboardType} = this.props

    const hasNewEnvironmentId = prevProps.environmentId !== environmentId
    const hasNewResourceId = prevProps.resourceId !== resourceId
    const hasNewTargetId = prevProps.targetId !== targetId
    const hasNewVersionId = prevProps.versionId !== versionId
    const hasNewDashboardType = prevProps.dashboardType !== dashboardType

    return hasNewEnvironmentId || hasNewResourceId || hasNewTargetId || hasNewVersionId || hasNewDashboardType
  }

  // Lifecycle handlers

  componentDidMount() {
    const {organizationId, environmentId, resourceId, versionId, resourceType, featuresCompatibility} = this.props

    if (featuresCompatibility || resourceType !== 'api') {
      return
    }
    const resId = `${resourceId}:${versionId}`
    this.props.viewCompatibility(organizationId, environmentId, resourceType, resId)
  }

  componentDidUpdate(prevProps: Readonly<AllProps>, prevState: Readonly<State>, prevContext: any): void {
    const {environmentId, resourceId, versionId, resourceType} = this.props
    const {environment, resource} = this.state

    if (this.hasUrlChanged(prevProps) && environment && resource) {
      this.onView(environmentId, resourceId, getComponentsResourceType(resourceType), versionId)
    }
  }

  componentWillReceiveProps(nextProps: Readonly<AllProps>, nextContext: any): void {
    const {environmentSelector, resourceSelector, versionSelector} = nextProps

    if (environmentSelector.selected && environmentSelector.resources && !this.state.environment) {
      this.setState({...this.state, environment: {selected: environmentSelector.selected, list: environmentSelector.resources}})
    }

    if (resourceSelector.selected && resourceSelector.resources && !this.state.resource) {
      this.setState({...this.state, resource: {selected: resourceSelector.selected, list: resourceSelector.resources}})
    }

    if (versionSelector.selected && versionSelector.resources && !this.state.version) {
      this.setState({...this.state, version: {selected: versionSelector.selected, list: versionSelector.resources}});
      this.handleDispatchViewedBuiltIn(resourceSelector.selected, versionSelector.selected.id.toString())
    }
  }

  componentWillMount(): void {
    const {environmentId, resourceId, resourceType, versionId, targetId, dashboardType } = this.props
    this.props.load({
      envId: environmentId, resourceId, resourceType, versionId, targetId, dashboardType,
      componentsResourceType: getComponentsResourceType(resourceType)
    })
    this.checkUrlTimeRange()

    if (!this.props.labels) {
      this.props.fetchLabels();
    }
  }

  componentWillUnmount(): void {
    this.onOpen()
  }

  render() {
    const {toasts} = this.props;
    const notificationSwitcherProps = this.getNotificationSwitcherProps();
    const builtInDashboardProps = this.getDashboardsProps();
    const saveAsModalProps = this.getSaveAsModalProps(builtInDashboardProps.selected, builtInDashboardProps.dashboards);

    return (
      <div className="monitoring-built-in-dashboards">
        <MonitoringToasts toasts={toasts}/>
        <CertificatesExpiryBanner />
        <BuiltInDashboardSaveAsModal {...saveAsModalProps} />
        <MonitoringBuiltInDashboard {...builtInDashboardProps}>
          <NotificationSwitcher {...notificationSwitcherProps}/>
        </MonitoringBuiltInDashboard>
      </div>
    )
  }
}
